<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const emit = defineEmits(['openNotificationPanel']);

const notificationMetadata = useMapGetter('notifications/getMeta');
const route = useRoute();
const { t } = useI18n();

const unreadCount = computed(() => {
  const count = Number(notificationMetadata.value?.unreadCount);
  return Number.isFinite(count) && count > 0 ? count : 0;
});

// Same cap as the sidebar count badges, so a busy workspace never widens the
// bell out of the topbar.
const displayCount = computed(() =>
  unreadCount.value > 99 ? '99+' : String(unreadCount.value)
);

const countLabel = computed(() =>
  t('INBOX.WORKSPACE.UNREAD_COUNT', { count: unreadCount.value })
);

// The count belongs in the button's own name: a button labelled only
// "Notifications" hides the badge from screen readers entirely.
const buttonLabel = computed(() =>
  unreadCount.value > 0 ? countLabel.value : t('TOPBAR.NOTIFICATIONS')
);

function openNotificationPanel() {
  if (route.name !== 'inbox_view') {
    emit('openNotificationPanel');
  }
}
</script>

<template>
  <button
    type="button"
    :aria-label="buttonLabel"
    class="size-8 rounded-lg hover:bg-n-alpha-1 flex-shrink-0 grid place-content-center relative"
    @click="openNotificationPanel"
  >
    <span class="i-lucide-bell size-4" />
    <span
      v-if="unreadCount > 0"
      data-test-id="notification-unread-badge"
      role="status"
      :aria-label="countLabel"
      class="absolute -top-1 ltr:-right-1.5 rtl:-left-1.5 inline-grid h-4 min-w-4 place-items-center rounded-full bg-n-ruby-11 px-1 text-[10px] font-semibold leading-none tabular-nums text-white ring-2 ring-n-background dark:bg-n-ruby-8"
    >
      {{ displayCount }}
    </span>
  </button>
</template>
